import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/layout/DataTable";
import { EmptyState } from "../../components/layout/EmptyState";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { SectionIntro } from "../../components/layout/SectionIntro";
import { ApiError, apiFetch } from "../../lib/api";
import { formatMoney } from "../../lib/format";
import {
  clearPendingIdempotencyKey,
  getOrCreatePendingIdempotencyKey,
  type PendingIdempotencyEntry,
} from "../../lib/idempotency";
import { normalizeNumericDraft, parseNumericDraft } from "../../lib/numericDraft";
import { readNullableStringParam } from "../../lib/url-state";
import type {
  DashboardResponse,
  FinanceDocument,
  FinanceDocumentListResponse,
  ForecastEventRow,
  ForecastEventsResponse,
  ForecastResponse,
  InventoryTransfer,
  InventoryTransferListResponse,
  LandedCostDocument,
  LandedCostDocumentListResponse,
  SettlementBatch,
  SettlementBatchListResponse,
  SupplierOrderProposal,
  SupplierOrderProposalListResponse,
  TransferProposal,
  TransferProposalListResponse,
} from "../../lib/types";
import { useDomainVersion } from "../../app/invalidation";

type SearchValue = string | number | boolean | null | undefined;

type FinanceRunContextSnapshot = {
  runId: string | null;
  supplierProposals: SupplierOrderProposal[];
  transferProposals: TransferProposal[];
  inventoryTransfers: InventoryTransfer[];
};
const FORECAST_BUILD_WARNING_MESSAGE =
  "Forecast run was created, but the cash event refresh failed. Retrying the same action is safe.";

function applySearchParamUpdates(searchParams: URLSearchParams, updates: Record<string, SearchValue>) {
  const next = new URLSearchParams(searchParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "" || value === false) {
      next.delete(key);
      return;
    }
    next.set(key, String(value));
  });
  return next;
}

function normalizeSearchParamValue(value: SearchValue): string | null {
  if (value === null || value === undefined || value === "" || value === false) {
    return null;
  }
  return String(value);
}

function useFinanceWorkspace() {
  const financeVersion = useDomainVersion("finance");
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forecastBuildWarnings, setForecastBuildWarnings] = useState<string[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [currentCashDraft, setCurrentCashDraft] = useState("50000");
  const [scenario, setScenario] = useState("base");
  const [includeApprovedPoCash, setIncludeApprovedPoCash] = useState(false);
  const [includeSupplierProposalCash, setIncludeSupplierProposalCash] = useState(true);
  const [includeTransferProposalCash, setIncludeTransferProposalCash] = useState(true);
  const [includeInventoryTransferCash, setIncludeInventoryTransferCash] = useState(true);
  const [includeRecommendationCash, setIncludeRecommendationCash] = useState(true);
  const [includeProjectedSettlements, setIncludeProjectedSettlements] = useState(true);
  const [includeStorageFees, setIncludeStorageFees] = useState(true);
  const [includeAdSpend, setIncludeAdSpend] = useState(true);
  const [includeVendorCredits, setIncludeVendorCredits] = useState(true);
  const [includeReimbursements, setIncludeReimbursements] = useState(true);
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [forecastEvents, setForecastEvents] = useState<ForecastEventRow[]>([]);
  const [defaultForecastMissing, setDefaultForecastMissing] = useState(false);
  const [supplierProposals, setSupplierProposals] = useState<SupplierOrderProposal[]>([]);
  const [transferProposals, setTransferProposals] = useState<TransferProposal[]>([]);
  const [inventoryTransfers, setInventoryTransfers] = useState<InventoryTransfer[]>([]);
  const [settlementBatches, setSettlementBatches] = useState<SettlementBatch[]>([]);
  const [financeDocuments, setFinanceDocuments] = useState<FinanceDocument[]>([]);
  const [landedCostDocuments, setLandedCostDocuments] = useState<LandedCostDocument[]>([]);

  const forecastRunId = readNullableStringParam(searchParams, "forecastRunId");
  const forecastRunIdRef = useRef(forecastRunId);
  const runIdRef = useRef(runId);
  const runContextRequestSeqRef = useRef(0);
  const forecastRequestSeqRef = useRef(0);
  const forecastEventsRequestSeqRef = useRef(0);
  const suppressedForecastRunIdRef = useRef<string | null>(null);
  const pendingMutationKeysRef = useRef<Record<string, PendingIdempotencyEntry>>({});
  const parsedCurrentCash = parseNumericDraft(currentCashDraft, "decimal");
  const currentCash = parsedCurrentCash.valid ? parsedCurrentCash.parsed : null;
  const currentCashValid = currentCash !== null;

  useEffect(() => {
    forecastRunIdRef.current = forecastRunId;
  }, [forecastRunId]);

  useEffect(() => {
    runIdRef.current = runId;
  }, [runId]);

  const supplierProposalCashSummary = useMemo(() => {
    const summary = new Map<string, number>();
    for (const event of forecastEvents) {
      if (event.reference_type !== "supplier_order_proposal") continue;
      const key = event.reference_id || "unknown";
      summary.set(key, (summary.get(key) || 0) + Number(event.amount));
    }
    return summary;
  }, [forecastEvents]);

  const transferProposalCashSummary = useMemo(() => {
    const summary = new Map<string, number>();
    for (const event of forecastEvents) {
      if (event.reference_type !== "transfer_proposal") continue;
      const key = event.reference_id || "unknown";
      summary.set(key, (summary.get(key) || 0) + Number(event.amount));
    }
    return summary;
  }, [forecastEvents]);

  const inventoryTransferCashSummary = useMemo(() => {
    const summary = new Map<string, number>();
    for (const event of forecastEvents) {
      if (event.reference_type !== "inventory_transfer") continue;
      const key = event.reference_id || "unknown";
      summary.set(key, (summary.get(key) || 0) + Number(event.amount));
    }
    return summary;
  }, [forecastEvents]);

  function clearRunContextTables() {
    setSupplierProposals([]);
    setTransferProposals([]);
    setInventoryTransfers([]);
  }

  function applyRunContextSnapshot(snapshot: FinanceRunContextSnapshot) {
    runIdRef.current = snapshot.runId;
    setRunId(snapshot.runId);
    setSupplierProposals(snapshot.supplierProposals);
    setTransferProposals(snapshot.transferProposals);
    setInventoryTransfers(snapshot.inventoryTransfers);
  }

  function invalidateRunContextRequests() {
    runContextRequestSeqRef.current += 1;
  }

  async function fetchDashboardRunId() {
    const data = await apiFetch<DashboardResponse>("/dashboard/replenishment");
    return data.run_id ?? null;
  }

  function clearForecastState() {
    setForecast(null);
    setForecastEvents([]);
  }

  function invalidateForecastRequests() {
    forecastRequestSeqRef.current += 1;
    forecastEventsRequestSeqRef.current += 1;
  }

  function patchCurrentSearchParams(updates: Record<string, SearchValue>) {
    if (Object.prototype.hasOwnProperty.call(updates, "forecastRunId")) {
      forecastRunIdRef.current = normalizeSearchParamValue(updates.forecastRunId);
    }
    setSearchParams((current) => applySearchParamUpdates(current, updates), { replace: true });
  }

  function hydrateForecastControls(payload: ForecastResponse) {
    const config = payload.run_config;
    setCurrentCashDraft(normalizeNumericDraft(config.current_cash_on_hand));
    setScenario(payload.scenario);
    setIncludeApprovedPoCash(Boolean(config.include_approved_pos));
    setIncludeSupplierProposalCash(Boolean(config.include_supplier_proposals));
    setIncludeTransferProposalCash(Boolean(config.include_transfer_proposals));
    setIncludeInventoryTransferCash(Boolean(config.include_inventory_transfers));
    setIncludeRecommendationCash(Boolean(config.include_recommendation_run));
    setIncludeProjectedSettlements(Boolean(config.include_projected_settlements));
    setIncludeStorageFees(Boolean(config.include_storage_fees));
    setIncludeAdSpend(Boolean(config.include_ad_spend));
    setIncludeVendorCredits(Boolean(config.include_vendor_credits));
    setIncludeReimbursements(Boolean(config.include_reimbursements));
  }

  function applyForecast(payload: ForecastResponse, options: { suppressFollowupLoad?: boolean } = {}) {
    setForecast(payload);
    hydrateForecastControls(payload);
    setError(null);
    setDefaultForecastMissing(false);
    if (forecastRunIdRef.current !== payload.run_id) {
      if (options.suppressFollowupLoad) {
        suppressedForecastRunIdRef.current = payload.run_id;
      }
      patchCurrentSearchParams({ forecastRunId: payload.run_id });
    }
  }

  async function loadForecast(activeForecastRunId?: string | null) {
    const requestId = ++forecastRequestSeqRef.current;
    forecastEventsRequestSeqRef.current += 1;
    const query = activeForecastRunId ? `?run_id=${activeForecastRunId}` : "";
    try {
      const payload = await apiFetch<ForecastResponse>(`/cash-forecast${query}`);
      if (requestId !== forecastRequestSeqRef.current) {
        return null;
      }
      applyForecast(payload, { suppressFollowupLoad: true });
      return payload;
    } catch (err) {
      if (requestId !== forecastRequestSeqRef.current) {
        return null;
      }
      throw err;
    }
  }

  async function loadForecastEvents(activeForecastRunId: string, options: { clearExisting?: boolean } = {}) {
    const requestId = ++forecastEventsRequestSeqRef.current;
    if (options.clearExisting) {
      setForecastEvents([]);
    }
    try {
      const payload = await apiFetch<ForecastEventsResponse>(`/cash-forecast/${activeForecastRunId}/events`);
      if (requestId !== forecastEventsRequestSeqRef.current) {
        return null;
      }
      setForecastEvents(payload.rows || []);
      return payload;
    } catch (err) {
      if (requestId !== forecastEventsRequestSeqRef.current) {
        return null;
      }
      throw err;
    }
  }

  async function fetchSupplierProposals(activeRunId: string) {
    const payload = await apiFetch<SupplierOrderProposalListResponse>(
      `/runs/recommendations/${activeRunId}/supplier-orders`,
    );
    return payload.items || [];
  }

  async function fetchTransferProposals(activeRunId: string) {
    const payload = await apiFetch<TransferProposalListResponse>(
      `/runs/recommendations/${activeRunId}/transfer-proposals`,
    );
    return payload.items || [];
  }

  async function fetchInventoryTransfers() {
    const payload = await apiFetch<InventoryTransferListResponse>("/inventory-transfers");
    return payload.items || [];
  }

  async function loadSettlementBatches() {
    const payload = await apiFetch<SettlementBatchListResponse>("/amazon-settlements");
    setSettlementBatches(payload.items || []);
  }

  async function loadFinanceDocuments() {
    const payload = await apiFetch<FinanceDocumentListResponse>("/finance-documents");
    setFinanceDocuments(payload.items || []);
  }

  async function loadLandedCostDocuments() {
    const payload = await apiFetch<LandedCostDocumentListResponse>("/landed-costs/documents");
    setLandedCostDocuments(payload.items || []);
  }

  async function refreshRunContext() {
    const requestId = ++runContextRequestSeqRef.current;

    try {
      const nextRunId = await fetchDashboardRunId();
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }

      if (!nextRunId) {
        applyRunContextSnapshot({
          runId: null,
          supplierProposals: [],
          transferProposals: [],
          inventoryTransfers: [],
        });
        return;
      }

      if (nextRunId !== runIdRef.current) {
        runIdRef.current = nextRunId;
        setRunId(nextRunId);
        clearRunContextTables();
      }

      const [supplierProposalItems, transferProposalItems, inventoryTransferItems] = await Promise.all([
        fetchSupplierProposals(nextRunId),
        fetchTransferProposals(nextRunId),
        fetchInventoryTransfers(),
      ]);

      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }

      applyRunContextSnapshot({
        runId: nextRunId,
        supplierProposals: supplierProposalItems,
        transferProposals: transferProposalItems,
        inventoryTransfers: inventoryTransferItems,
      });
    } catch (err) {
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function refreshFinance() {
    await Promise.all([refreshRunContext(), loadSettlementBatches(), loadFinanceDocuments(), loadLandedCostDocuments()]);
  }

  async function handleForecast() {
    const activeRunId = runIdRef.current;
    if (!activeRunId || currentCash === null) return;
    setLoading(true);
    setError(null);
    setForecastBuildWarnings([]);
    const requestBody = JSON.stringify({
      recommendation_run_id: activeRunId,
      current_cash_on_hand: currentCash,
      scenario,
      include_open_pos: true,
      include_approved_pos: includeApprovedPoCash,
      include_supplier_proposals: includeSupplierProposalCash,
      include_transfer_proposals: includeTransferProposalCash,
      include_inventory_transfers: includeInventoryTransferCash,
      include_recommendation_run: includeRecommendationCash,
      include_projected_settlements: includeProjectedSettlements,
      include_storage_fees: includeStorageFees,
      include_ad_spend: includeAdSpend,
      include_vendor_credits: includeVendorCredits,
      include_reimbursements: includeReimbursements,
    });
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      "forecast-run",
      requestBody,
    );
    try {
      const data = await apiFetch<ForecastResponse>("/cash-forecast/run", {
        method: "POST",
        body: requestBody,
        headers: { "Idempotency-Key": key },
      });
      invalidateRunContextRequests();
      invalidateForecastRequests();
      applyForecast(data, { suppressFollowupLoad: true });
      try {
        await loadForecastEvents(data.run_id, { clearExisting: true });
        clearPendingIdempotencyKey(pendingMutationKeysRef.current, "forecast-run");
      } catch {
        setForecastBuildWarnings([FORECAST_BUILD_WARNING_MESSAGE]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshFinance().catch((err) => setError((err as Error).message));
  }, [financeVersion]);

  useEffect(() => {
    if (forecastRunId && suppressedForecastRunIdRef.current === forecastRunId) {
      suppressedForecastRunIdRef.current = null;
      return;
    }
    if (forecastRunId) {
      loadForecast(forecastRunId)
        .then((payload) => {
          if (!payload) return null;
          return loadForecastEvents(payload.run_id, { clearExisting: true });
        })
        .catch((err) => {
          clearForecastState();
          setDefaultForecastMissing(false);
          setError((err as Error).message);
        });
      return;
    }
    loadForecast()
      .then((payload) => {
        if (!payload) return null;
        return loadForecastEvents(payload.run_id, { clearExisting: true });
      })
      .catch((err) => {
        clearForecastState();
        if (err instanceof ApiError && err.status === 404) {
          setDefaultForecastMissing(true);
          setError(null);
          return;
        }
        setDefaultForecastMissing(false);
        setError((err as Error).message);
      });
  }, [forecastRunId]);

  return {
    loading,
    error,
    forecastBuildWarnings,
    currentCashDraft,
    setCurrentCashDraft,
    currentCashValid,
    scenario,
    setScenario,
    includeApprovedPoCash,
    setIncludeApprovedPoCash,
    includeSupplierProposalCash,
    setIncludeSupplierProposalCash,
    includeTransferProposalCash,
    setIncludeTransferProposalCash,
    includeInventoryTransferCash,
    setIncludeInventoryTransferCash,
    includeRecommendationCash,
    setIncludeRecommendationCash,
    includeProjectedSettlements,
    setIncludeProjectedSettlements,
    includeStorageFees,
    setIncludeStorageFees,
    includeAdSpend,
    setIncludeAdSpend,
    includeVendorCredits,
    setIncludeVendorCredits,
    includeReimbursements,
    setIncludeReimbursements,
    forecast,
    forecastEvents,
    supplierProposals,
    transferProposals,
    inventoryTransfers,
    settlementBatches,
    financeDocuments,
    landedCostDocuments,
    supplierProposalCashSummary,
    transferProposalCashSummary,
    inventoryTransferCashSummary,
    handleForecast,
    runId,
    defaultForecastMissing,
  };
}

export function FinanceWorkspace() {
  const state = useFinanceWorkspace();
  const forecastSummary = state.forecast
    ? (() => {
        const endingBalances = state.forecast.rows.map((row) => Number(row.ending_balance || 0));
        const minEndingBalance = endingBalances.length > 0 ? Math.min(...endingBalances) : 0;
        const negativeWeeks = state.forecast.rows.filter((row) => Number(row.ending_balance || 0) < 0);
        const largestOutflowWeek = [...state.forecast.rows].sort((a, b) => Number(b.outflows || 0) - Number(a.outflows || 0))[0] || null;
        const summary: string[] = [];
        summary.push(`Lowest projected ending balance is ${formatMoney(minEndingBalance)}.`);
        if (negativeWeeks.length > 0) {
          summary.push(`${negativeWeeks.length} forecast week(s) dip below zero cash.`);
        } else {
          summary.push("No forecast week currently dips below zero cash.");
        }
        if (largestOutflowWeek) {
          summary.push(`Largest projected outflow week starts ${largestOutflowWeek.week_start} at ${formatMoney(largestOutflowWeek.outflows)}.`);
        }
        summary.push(`Scenario is ${state.forecast.scenario} with payout source ${state.forecast.payout_source || 'unknown'}.`);
        return summary;
      })()
    : [];

  return (
    <>
      <SectionIntro
        eyebrow="Finance"
        title="Will cash stay healthy?"
        description="Get a clear forward view of cash so you can spot tight weeks early and understand what is driving the change."
      />
      <Panel className="forecast workspace-control-panel">
        <h2>Cash outlook</h2>
        <div className="forecast-controls">
          <label>
            Current Cash
            <input
              type="number"
              step="0.01"
              value={state.currentCashDraft}
              onChange={(event) => state.setCurrentCashDraft(event.target.value)}
            />
          </label>
          <label>
            Scenario
            <select value={state.scenario} onChange={(event) => state.setScenario(event.target.value)}>
              <option value="base">Base</option>
              <option value="conservative_demand">Conservative Demand</option>
              <option value="delayed_inbound">Delayed Inbound</option>
            </select>
          </label>
          <button onClick={() => state.handleForecast()} disabled={state.loading || !state.runId || !state.currentCashValid}>
            Refresh Outlook
          </button>
        </div>
        <details className="workspace-details">
          <summary>Advanced cash assumptions</summary>
          <div className="forecast-controls">
          <label className="checkbox-label">
            <span>Include Approved Draft Cash</span>
            <input type="checkbox" checked={state.includeApprovedPoCash} onChange={(event) => state.setIncludeApprovedPoCash(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Supplier Proposal Cash</span>
            <input type="checkbox" checked={state.includeSupplierProposalCash} onChange={(event) => state.setIncludeSupplierProposalCash(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Transfer Proposal Cash</span>
            <input type="checkbox" checked={state.includeTransferProposalCash} onChange={(event) => state.setIncludeTransferProposalCash(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Inventory Transfer Cash</span>
            <input type="checkbox" checked={state.includeInventoryTransferCash} onChange={(event) => state.setIncludeInventoryTransferCash(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Recommended Cash</span>
            <input type="checkbox" checked={state.includeRecommendationCash} onChange={(event) => state.setIncludeRecommendationCash(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Projected Settlements</span>
            <input type="checkbox" checked={state.includeProjectedSettlements} onChange={(event) => state.setIncludeProjectedSettlements(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Storage Fees</span>
            <input type="checkbox" checked={state.includeStorageFees} onChange={(event) => state.setIncludeStorageFees(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Ad Spend</span>
            <input type="checkbox" checked={state.includeAdSpend} onChange={(event) => state.setIncludeAdSpend(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Vendor Credits</span>
            <input type="checkbox" checked={state.includeVendorCredits} onChange={(event) => state.setIncludeVendorCredits(event.target.checked)} />
          </label>
          <label className="checkbox-label">
            <span>Include Reimbursements</span>
            <input type="checkbox" checked={state.includeReimbursements} onChange={(event) => state.setIncludeReimbursements(event.target.checked)} />
          </label>
          </div>
        </details>

        {state.forecast ? (
          <>
            <div className="detail-cards">
              <MetricCard label="Forecast Run" value={state.forecast.run_id} />
              <MetricCard label="Scenario" value={state.forecast.scenario} />
              <MetricCard label="Payout Source" value={state.forecast.payout_source || "unknown"} />
            </div>
            <WarningPanel title="Things to review" warnings={[...(state.forecast.warnings || []), ...state.forecastBuildWarnings].slice(0, 6)} />

            {forecastSummary.length > 0 && (
              <Panel>
                <h3>What to know right now</h3>
                <ul className="summary-list">
                  {forecastSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Panel>
            )}

            <Panel className="workspace-control-panel recommended-action-panel">
              <h3>Recommended next step</h3>
              <p className="muted-small">Refresh the outlook after major plan changes, then review the lowest cash week first.</p>
              <div className="actions">
                <button className="button-primary-action" onClick={() => state.handleForecast()} disabled={state.loading || !state.runId || !state.currentCashValid}>
                  Refresh Outlook
                </button>
              </div>
            </Panel>

            <table>
              <thead>
                <tr>
                  <th>Week Start</th>
                  <th>Inflows</th>
                  <th>Outflows</th>
                  <th>Ending Balance</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {state.forecast.rows.map((row) => (
                  <tr key={row.week_start}>
                    <td>{row.week_start}</td>
                    <td>{formatMoney(row.inflows)}</td>
                    <td>{formatMoney(row.outflows)}</td>
                    <td className={Number(row.ending_balance) < 0 ? "negative" : ""}>{formatMoney(row.ending_balance)}</td>
                    <td>{row.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {state.forecast.category_breakdown && state.forecast.category_breakdown.length > 0 && (
              <>
                <h3>Weekly Category Breakdown</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Category</th>
                        <th>Inflows</th>
                        <th>Outflows</th>
                        <th>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.forecast.category_breakdown.map((row, index) => (
                        <tr key={`forecast-breakdown-${row.week_start}-${row.category}-${index}`}>
                          <td>{row.week_start}</td>
                          <td>{row.category}</td>
                          <td>{formatMoney(row.inflows)}</td>
                          <td>{formatMoney(row.outflows)}</td>
                          <td>{formatMoney(row.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}

            {state.forecast.reserve_rollforward && state.forecast.reserve_rollforward.length > 0 && (
              <>
                <h3>Reserve Rollforward</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Disbursement</th>
                        <th>Source</th>
                        <th>Opening</th>
                        <th>Held</th>
                        <th>Released</th>
                        <th>Closing</th>
                        <th>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.forecast.reserve_rollforward.map((row) => (
                        <tr key={`reserve-${row.label}`}>
                          <td>{row.disbursement_date}</td>
                          <td>{row.source}</td>
                          <td>{formatMoney(row.opening_balance)}</td>
                          <td>{formatMoney(row.held)}</td>
                          <td>{formatMoney(row.released)}</td>
                          <td>{formatMoney(row.closing_balance)}</td>
                          <td>{row.confidence || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}

            {state.forecast.feedback_rows && state.forecast.feedback_rows.length > 0 && (
              <>
                <h3>Forecast vs Actual Cash</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Forecast End</th>
                        <th>Actual End</th>
                        <th>Bias</th>
                        <th>Completeness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.forecast.feedback_rows.map((row) => (
                        <tr key={`feedback-week-${row.week_start}`}>
                          <td>{row.week_start}</td>
                          <td>{formatMoney(row.forecast_ending_balance)}</td>
                          <td>{formatMoney(row.actual_ending_balance)}</td>
                          <td>{formatMoney(row.balance_bias)}</td>
                          <td>{row.completeness_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}

            <h3>Cash Events</h3>
            <DataTable slim>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Layer</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Direction</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Stage</th>
                    <th>Amount</th>
                    <th>Reference</th>
                    <th>Counterparty</th>
                    <th>Confidence</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {state.forecastEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{event.event_date}</td>
                      <td>{event.planning_layer || "-"}</td>
                      <td>{event.event_type}</td>
                      <td>{event.event_category || "-"}</td>
                      <td>{event.cash_direction || "-"}</td>
                      <td>{event.event_status || "-"}</td>
                      <td>{event.source_type || "-"}</td>
                      <td>{event.reference_stage || "-"}</td>
                      <td>{formatMoney(event.amount)}</td>
                      <td>{event.reference_type || "-"}</td>
                      <td>{event.counterparty || "-"}</td>
                      <td>{event.confidence || "-"}</td>
                      <td>{event.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>

            {state.includeSupplierProposalCash && state.supplierProposals.length > 0 && (
              <>
                <h3>Supplier Proposal Cash by Proposal</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Supplier</th>
                        <th>Wave</th>
                        <th>Status</th>
                        <th>Immediate Cash</th>
                        <th>Forecast Layer Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.supplierProposals.map((proposal) => (
                        <tr key={`proposal-cash-${proposal.id}`}>
                          <td>{proposal.supplier}</td>
                          <td>{proposal.proposal_order_date}</td>
                          <td>{proposal.proposal_status}</td>
                          <td>{formatMoney(proposal.immediate_cash_required)}</td>
                          <td>{formatMoney(state.supplierProposalCashSummary.get(proposal.id) || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}

            {state.includeTransferProposalCash && state.transferProposals.length > 0 && (
              <>
                <h3>Transfer Proposal Cash by Wave</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Origin</th>
                        <th>Ship Date</th>
                        <th>Status</th>
                        <th>Transfer Cost</th>
                        <th>Forecast Layer Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.transferProposals.map((proposal) => (
                        <tr key={`transfer-proposal-cash-${proposal.id}`}>
                          <td>{proposal.origin_key}</td>
                          <td>{proposal.proposal_ship_date}</td>
                          <td>{proposal.proposal_status}</td>
                          <td>{formatMoney(proposal.cash_required_now)}</td>
                          <td>{formatMoney(state.transferProposalCashSummary.get(proposal.id) || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}

            {state.includeInventoryTransferCash && state.inventoryTransfers.length > 0 && (
              <>
                <h3>Committed Transfer Cash</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Origin</th>
                        <th>Status</th>
                        <th>Ship Date</th>
                        <th>Units</th>
                        <th>Forecast Layer Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.inventoryTransfers.map((transfer) => (
                        <tr key={`inventory-transfer-cash-${transfer.id}`}>
                          <td>{transfer.origin_key}</td>
                          <td>{transfer.status}</td>
                          <td>{transfer.actual_ship_date || transfer.planned_ship_date || "-"}</td>
                          <td>{transfer.lines.reduce((sum, line) => sum + line.units, 0)}</td>
                          <td>{formatMoney(state.inventoryTransferCashSummary.get(transfer.id) || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}
          </>
        ) : state.defaultForecastMissing ? (
          <EmptyState
            title="No cash outlook yet"
            message="Enter current cash and refresh the outlook to see upcoming cash pressure, inflows, and outflows."
            compact
            actionLabel="Refresh Outlook"
            onAction={() => void state.handleForecast()}
          />
        ) : null}
      </Panel>

      <Panel>
        <h2>Amazon Settlements</h2>
        <DataTable slim>
          <table>
            <thead>
              <tr>
                <th>Disbursement</th>
                <th>Source</th>
                <th>Status</th>
                <th>Gross</th>
                <th>Fees</th>
                <th>Storage</th>
                <th>Ads</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {state.settlementBatches.length > 0 ? (
                state.settlementBatches.slice(0, 20).map((batch) => (
                  <tr key={batch.id}>
                    <td>{batch.disbursement_date}</td>
                    <td>{batch.source}</td>
                    <td>{batch.status}</td>
                    <td>{formatMoney(batch.gross_sales)}</td>
                    <td>{formatMoney(batch.fees_total)}</td>
                    <td>{formatMoney(batch.storage_total)}</td>
                    <td>{formatMoney(batch.ads_total)}</td>
                    <td>{formatMoney(batch.net_payout)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>No settlement batches yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </Panel>

      <Panel>
        <h2>Finance Documents</h2>
        <DataTable slim>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Status</th>
                <th>Counterparty</th>
                <th>Channel</th>
                <th>Reference</th>
                <th>Lines</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {state.financeDocuments.length > 0 ? (
                state.financeDocuments.slice(0, 20).map((document) => (
                  <tr key={document.id}>
                    <td>{document.paid_date || document.application_date || document.payable_date || document.document_date}</td>
                    <td>{document.document_type}</td>
                    <td>{document.status}</td>
                    <td>{document.counterparty || document.vendor || "-"}</td>
                    <td>{document.channel || "-"}</td>
                    <td>{document.document_ref || document.id.slice(0, 8)}</td>
                    <td>{document.lines.length}</td>
                    <td>
                      {formatMoney(
                        document.lines.reduce((sum, line) => {
                          const signed = line.cash_direction === "inflow" ? Number(line.amount || 0) : -Number(line.amount || 0);
                          return sum + signed;
                        }, 0),
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>No finance documents yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </Panel>

      <Panel>
        <h2>Actual Landed Cost Documents</h2>
        <DataTable slim>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Status</th>
                <th>Vendor</th>
                <th>Reference</th>
                <th>Charges</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {state.landedCostDocuments.length > 0 ? (
                state.landedCostDocuments.slice(0, 20).map((document) => (
                  <tr key={document.id}>
                    <td>{document.document_date}</td>
                    <td>{document.document_type}</td>
                    <td>{document.status}</td>
                    <td>{document.vendor || "-"}</td>
                    <td>{document.document_ref || document.id.slice(0, 8)}</td>
                    <td>{document.charges.length}</td>
                    <td>{formatMoney(document.charges.reduce((sum, charge) => sum + Number(charge.amount || 0), 0))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>No landed cost documents yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </Panel>

      {state.error && <WarningPanel title="Cash needs attention" warnings={[state.error]} />}
    </>
  );
}
